// Admin "Generate Menu Image" — one WhatsApp/print flyer of the live food board.
// Photos stay on /tv-menu. Names, prices, italic choice modifiers stacked under
// the item (Pizza: Chicken, Meat Lovers; Loaded Fries: Chicken, Beef), and paid
// add-ons such as +Chips($2).
// Drinks are never included.

export const FLYER_BURGUNDY = '#8b1a2b';
export const FLYER_GOLD = '#d4a855';
export const FLYER_CREAM = '#fffdf8';
export const FLYER_TEXT = '#2b2113';

function isOn(v) {
  return v === true || v === 1 || v === '1';
}

function sortItems(a, b) {
  const so = (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
  if (so !== 0) return so;
  return String(a.name || '').localeCompare(String(b.name || ''));
}

export function formatFlyerPrice(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0';
  if (Math.abs(n - Math.round(n)) < 0.001) return `$${Math.round(n)}`;
  return `$${n.toFixed(2)}`;
}

export function formatFlyerAddOn(option) {
  const raw = String(option?.name || '').trim().replace(/^\+\s*/, '');
  if (!raw) return '';
  return `+${raw}(${formatFlyerPrice(option.surcharge)})`;
}

function isGenericOptionName(name) {
  return /^(yes|no|add|on|off)$/i.test(String(name || '').trim());
}

function assignedGroups(item, modifierGroups) {
  const groupIds = Array.isArray(item?.modifier_group_ids) ? item.modifier_group_ids : [];
  if (groupIds.length === 0 || !modifierGroups.length) return [];
  const byId = new Map(modifierGroups.map((g) => [g.id, g]));
  const groups = [];
  for (const gid of groupIds) {
    const group = byId.get(gid);
    if (group) groups.push(group);
  }
  return groups;
}

function optionInStock(group, option) {
  if (!isOn(group?.track_stock)) return true;
  const qty = option?.stock_quantity;
  if (qty === null || qty === undefined || qty === '') return true;
  const n = Number(qty);
  if (!Number.isFinite(n)) return true;
  return n > 0;
}

function definedChoiceOptions(group) {
  return (group?.options || []).filter((option) => {
    const name = String(option?.name || '').trim();
    return name && !isGenericOptionName(name);
  });
}

// A choice group is a modifier the customer picks from (pizza flavour,
// loaded-fries protein). A single paid extra stays an add-on: +Chips($2).
export function isFlyerChoiceGroup(group) {
  return definedChoiceOptions(group).length >= 2;
}

function isAddOnGroup(group) {
  if (!group || isFlyerChoiceGroup(group)) return false;
  const opts = group.options || [];
  if (!isOn(group.required)) return true;
  return opts.length > 0 && opts.every((o) => Number(o.surcharge) > 0);
}

export function formatFlyerChoice(option) {
  const name = String(option?.name || '').trim();
  if (!name) return '';
  const surcharge = Number(option.surcharge);
  if (Number.isFinite(surcharge) && surcharge > 0) {
    return `${name} (+${formatFlyerPrice(surcharge)})`;
  }
  return name;
}

export function flyerChoicesForItem(item, modifierGroups = []) {
  return assignedGroups(item, modifierGroups)
    .filter(isFlyerChoiceGroup)
    .map((group) => ({
      name: String(group.name || '').trim(),
      options: definedChoiceOptions(group)
        .filter((option) => optionInStock(group, option))
        .map((option) => ({
          name: String(option.name).trim(),
          surcharge: Number(option.surcharge) || 0,
        })),
    }))
    .filter((group) => group.options.length > 0);
}

export function flyerChoiceLines(choices) {
  const groups = Array.isArray(choices) ? choices : [];
  const lines = [];
  for (const group of groups) {
    for (const option of group.options || []) {
      const line = formatFlyerChoice(option);
      if (line) lines.push(line);
    }
  }
  return lines;
}

export function flyerAddOnsForItem(item, modifierGroups = []) {
  const addOns = [];
  const seen = new Set();
  for (const group of assignedGroups(item, modifierGroups)) {
    if (!isAddOnGroup(group)) continue;
    for (const opt of group.options || []) {
      const surcharge = Number(opt.surcharge);
      if (!Number.isFinite(surcharge) || surcharge <= 0) continue;
      const key = `${String(opt.name || '').trim().toLowerCase()}|${surcharge}`;
      if (seen.has(key)) continue;
      seen.add(key);
      addOns.push({ name: opt.name, surcharge });
    }
  }
  return addOns;
}

export function groupFoodMenuForFlyer(items, categories = [], { fasting = false, modifierGroups = [] } = {}) {
  const catById = new Map((categories || []).map((c) => [c.id, c]));
  const barbara = [];
  const noufer = [];
  const cafe = [];

  for (const item of items || []) {
    if (!isOn(item.active)) continue;
    const cat = catById.get(item.category_id) || {};
    if (cat.id && cat.active !== undefined && !isOn(cat.active)) continue;

    const venue = item.venue || cat.venue || '';
    const kitchen = item.kitchen || cat.kitchen || '';
    const isDrinks = isOn(item.is_drinks) || isOn(cat.is_drinks) || isOn(cat.is_hot_drink);
    const atCounter = venue === 'counter' || isOn(cat.collected_at_counter);
    if (atCounter) continue;
    if (venue === 'desserts') continue;
    if (venue === 'cafe' && isDrinks) continue;

    const diet = item.diet_type || 'non_fasting';
    if (fasting && diet === 'non_fasting') continue;
    if (!fasting && diet === 'fasting') continue;

    const row = {
      id: item.id,
      name: String(item.name || '').trim(),
      price: Number(item.price) || 0,
      sort_order: Number(item.sort_order) || 0,
      addOns: flyerAddOnsForItem(item, modifierGroups),
      choices: flyerChoicesForItem(item, modifierGroups),
    };
    if (!row.name) continue;

    if (venue === 'cafe') cafe.push(row);
    else if (kitchen === 'st_barbara') barbara.push(row);
    else if (kitchen === 'st_noufer') noufer.push(row);
  }

  barbara.sort(sortItems);
  noufer.sort(sortItems);
  cafe.sort(sortItems);
  return { barbara, noufer, cafe };
}

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function wrapName(ctx, name, maxWidth) {
  if (ctx.measureText(name).width <= maxWidth) return [name];
  const words = name.split(/\s+/);
  const lines = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (ctx.measureText(next).width <= maxWidth) {
      current = next;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  if (lines.length <= 2) return lines;
  let last = lines.slice(1).join(' ');
  while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) {
    last = last.slice(0, -1);
  }
  return [lines[0], `${last}…`];
}

function drawHeading(ctx, title, x, y, width) {
  ctx.save();
  ctx.fillStyle = FLYER_BURGUNDY;
  ctx.font = '600 48px Georgia, "Times New Roman", serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(title, x + width / 2, y);
  ctx.strokeStyle = FLYER_GOLD;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x + width * 0.2, y + 16);
  ctx.lineTo(x + width * 0.8, y + 16);
  ctx.stroke();
  ctx.restore();
  return y + 52;
}

const NAME_FONT = '500 30px "Segoe UI", system-ui, sans-serif';
const CHOICE_FONT = 'italic 500 30px "Segoe UI", system-ui, sans-serif';
const ADDON_FONT = '500 22px "Segoe UI", system-ui, sans-serif';
const PRICE_FONT = '600 30px "Segoe UI", system-ui, sans-serif';
const CHOICE_LINE_H = 46;

function addOnText(item) {
  return (item.addOns || []).map(formatFlyerAddOn).filter(Boolean).join('  ');
}

function layoutRow(ctx, item, width) {
  const priceW = 118;
  const padX = 16;
  const extras = addOnText(item);
  ctx.font = ADDON_FONT;
  const extraW = extras ? Math.min(220, ctx.measureText(extras).width + 16) : 0;
  const nameW = Math.max(120, width - priceW - extraW - padX * 2);
  ctx.font = NAME_FONT;
  const nameLines = wrapName(ctx, item.name, nameW);
  const choiceW = Math.max(120, width - priceW - padX * 2 - 12);
  ctx.font = CHOICE_FONT;
  const choiceLines = [];
  for (const label of flyerChoiceLines(item.choices)) {
    choiceLines.push(...wrapName(ctx, label, choiceW));
  }
  const nameBlockH = Math.max(56, nameLines.length * 34 + 20);
  const choiceH = choiceLines.length * CHOICE_LINE_H;
  return { extras, nameLines, choiceLines, rowH: nameBlockH + choiceH, nameBlockH, priceW, padX };
}

function drawRows(ctx, items, x, y, width) {
  let cursor = y;
  items.forEach((item, i) => {
    const { extras, nameLines, choiceLines, rowH, nameBlockH, priceW, padX } = layoutRow(ctx, item, width);
    if (i % 2 === 1) {
      ctx.fillStyle = 'rgba(43, 33, 19, 0.06)';
      ctx.fillRect(x, cursor, width, rowH);
    }
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = FLYER_TEXT;
    ctx.font = NAME_FONT;
    nameLines.forEach((line, li) => {
      const ly = cursor + (nameBlockH / (nameLines.length + 1)) * (li + 1);
      ctx.fillText(line, x + padX, ly);
    });
    if (choiceLines.length) {
      ctx.fillStyle = FLYER_TEXT;
      ctx.font = CHOICE_FONT;
      choiceLines.forEach((line, li) => {
        const ly = cursor + nameBlockH + li * CHOICE_LINE_H + CHOICE_LINE_H / 2;
        ctx.fillText(line, x + padX + 12, ly);
      });
    }
    const midY = cursor + nameBlockH / 2;
    ctx.textAlign = 'right';
    if (extras) {
      ctx.fillStyle = 'rgba(43, 33, 19, 0.55)';
      ctx.font = ADDON_FONT;
      ctx.fillText(extras, x + width - padX - priceW, midY);
    }
    ctx.fillStyle = FLYER_TEXT;
    ctx.font = PRICE_FONT;
    ctx.fillText(formatFlyerPrice(item.price), x + width - padX, midY);
    cursor += rowH;
  });
  return cursor;
}

function drawColumn(ctx, title, items, x, y, width) {
  let cursor = drawHeading(ctx, title, x, y, width);
  return drawRows(ctx, items, x, cursor, width);
}

async function paintFlyer(grouped, logo) {
  const { barbara, noufer, cafe } = grouped;
  const W = 1920;
  const pad = 72;
  const gutter = 56;
  const colW = (W - pad * 2 - gutter) / 2;
  const leftX = pad;
  const rightX = pad + colW + gutter;
  const headerH = 250;
  const measure = document.createElement('canvas').getContext('2d');
  const estimateH = (items) => items.reduce((h, item) => h + layoutRow(measure, item, colW).rowH, 0);
  const leftH = 70 + (barbara.length ? estimateH(barbara) : 56);
  const rightH = 70 + (noufer.length ? estimateH(noufer) : 56)
    + 48
    + 70 + (cafe.length ? estimateH(cafe) : 56);
  const H = Math.max(1080, headerH + Math.max(leftH, rightH) + pad);

  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = W * scale;
  canvas.height = H * scale;
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);

  ctx.fillStyle = FLYER_CREAM;
  ctx.fillRect(0, 0, W, H);

  const cx = W / 2;
  const logoY = 28;
  const logoSize = 132;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, logoY + logoSize / 2, logoSize / 2 + 6, 0, Math.PI * 2);
  ctx.fillStyle = '#fff';
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = FLYER_GOLD;
  ctx.stroke();
  ctx.restore();
  if (logo) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(logo, cx - logoSize / 2, logoY, logoSize, logoSize);
    ctx.restore();
  }

  ctx.fillStyle = FLYER_BURGUNDY;
  ctx.font = '600 26px "Segoe UI", system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('ST BARBARA  ·  ST NOUFER', cx, logoY + logoSize + 36);
  ctx.fillStyle = FLYER_GOLD;
  ctx.font = '700 16px "Segoe UI", system-ui, sans-serif';
  ctx.fillText('KITCHEN  &  CAFE', cx, logoY + logoSize + 64);

  const bodyY = headerH;
  ctx.strokeStyle = 'rgba(212, 168, 85, 0.45)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(pad + colW + gutter / 2, bodyY);
  ctx.lineTo(pad + colW + gutter / 2, H - pad + 8);
  ctx.stroke();

  drawColumn(ctx, 'St Barbara', barbara, leftX, bodyY, colW);
  let afterNoufer = bodyY;
  if (noufer.length) {
    afterNoufer = drawColumn(ctx, 'St Noufer', noufer, rightX, bodyY, colW);
  }
  if (cafe.length) {
    const cafeY = noufer.length ? afterNoufer + 36 : bodyY;
    drawColumn(ctx, 'The Holy Ground', cafe, rightX, cafeY, colW);
  }

  return canvas;
}

export async function renderFoodMenuCanvas(grouped) {
  const logo = await loadImage('/logo-tv.png') || await loadImage('/logo.png');
  return paintFlyer(grouped, logo);
}

export async function downloadFoodMenuImage(grouped) {
  const canvas = await renderFoodMenuCanvas(grouped);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not create image'))), 'image/png');
  });
  const stamp = new Date();
  const y = stamp.getFullYear();
  const m = String(stamp.getMonth() + 1).padStart(2, '0');
  const d = String(stamp.getDate()).padStart(2, '0');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `st-barb-menu-${y}-${m}-${d}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
